import type { FilterModel, FilterOperator, SortModel } from 'components/ColumnFilters/types';

export interface ColumnFilterPayloadItem {
  id: string;
  operator: string;
  values: Array<string | number | boolean | null>;
  selOptions: Array<string | number | boolean | null>;
}

export interface TableSortingInfo {
  sortBy: string;
  sortDir: string;
}

// Same payload shape as Pipeline Run Status so the backend can reuse its column filter handling
export const toColumnFiltersPayload = (filterModel: FilterModel): ColumnFilterPayloadItem[] =>
  Object.entries(filterModel).map(([id, filter]) => {
    const condition = filter.condition;
    const conditionValues = condition
      ? [condition.value1, condition.value2].filter((value) => value !== null && value !== undefined)
      : [];
    const values = condition ? conditionValues : (filter.values ?? []);

    return {
      id,
      operator: condition?.operator ?? 'equals',
      values: values as ColumnFilterPayloadItem['values'],
      selOptions: filter.selOptions ?? (condition ? [] : values as ColumnFilterPayloadItem['selOptions']),
    };
  });

export const mergeColumnFilterPayload = (
  current: ColumnFilterPayloadItem[],
  next: ColumnFilterPayloadItem[],
  changedColumnKey: string | null
): ColumnFilterPayloadItem[] => {
  if (!changedColumnKey) return next;

  const nextById = new Map(next.map((filter) => [filter.id, filter]));
  const merged = current.filter(
    (filter) => filter.id !== changedColumnKey && !nextById.has(filter.id)
  );
  return [...merged, ...next];
};

export const columnFilterPayloadToModel = (payload: ColumnFilterPayloadItem[] = []): FilterModel =>
  payload.reduce<FilterModel>((model, filter) => {
    const hasCondition = (filter.operator && filter.operator !== 'equals') ||
      (filter.operator === 'equals' && !filter.selOptions?.length);
    model[filter.id] = {
      values: hasCondition ? undefined : filter.values ?? [],
      selOptions: filter.selOptions ?? [],
      condition: hasCondition
        ? {
            operator: filter.operator as NonNullable<FilterModel[string]['condition']>['operator'],
            value1: (filter.values?.[0] ?? null) as string | number | null,
            value2: (filter.values?.[1] ?? null) as string | number | null,
          }
        : undefined,
    };
    return model;
  }, {});

export const loadColumnFilterPayload = (storageKey: string): ColumnFilterPayloadItem[] => {
  try {
    const saved = sessionStorage.getItem(storageKey);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error reading column filter state:', error);
    return [];
  }
};

export const saveColumnFilterPayload = (storageKey: string, payload: ColumnFilterPayloadItem[]): void => {
  try {
    if (payload.length > 0) {
      sessionStorage.setItem(storageKey, JSON.stringify(payload));
    } else {
      sessionStorage.removeItem(storageKey);
    }
  } catch (error) {
    console.error('Error saving column filter state:', error);
  }
};

const FREE_TEXT_OPERATORS: FilterOperator[] = ['equals', 'neq', 'startswith', 'contains', 'notcontains'];
const FIXED_VALUE_OPERATORS: FilterOperator[] = ['equals', 'neq'];

// Free-text columns get every text operator; fixed-value columns (severity, status, ...) only get exact matching.
// Column definitions stay untouched: the filter settings are layered on top of them.
export const withColumnFilterOperators = <T extends { key: string }>(columns: T[], freeTextKeys: string[]) =>
  columns.map((column) => ({
    ...column,
    filterOperators: freeTextKeys.includes(column.key) ? FREE_TEXT_OPERATORS : FIXED_VALUE_OPERATORS,
  }));

// Clearing the column sort falls back to the table's own default order instead of an unsorted request
export const getSortingFromSortModel = (sortModel: SortModel, defaultSorting: TableSortingInfo): TableSortingInfo =>
  sortModel.length
    ? { sortBy: sortModel[0].columnKey, sortDir: sortModel[0].direction }
    : defaultSorting;
